import { useRef, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const COMPLETION_THRESHOLD = 95;

const createFlags = () => ({
  sentStarted: false,
  sent25: false,
  sent50: false,
  sent75: false,
  sentCompleted: false,
});

const EVENT_TO_FLAG = {
  episode_started: 'sentStarted',
  progress_25: 'sent25',
  progress_50: 'sent50',
  progress_75: 'sent75',
  episode_completed: 'sentCompleted',
};

/** Completion always reports a full view: 100% and total episode duration. */
const getCompletionMetrics = (videoElement) => {
  const duration = videoElement?.duration;
  const watchSeconds =
    duration && Number.isFinite(duration) && duration > 0
      ? duration
      : videoElement?.currentTime || 0;
  return { watchSeconds, progressPercent: 100 };
};

/**
 * Storyverse / Pixley Originals playback analytics.
 * One session per playback start; exactly 5 events max per session.
 */
export function useStoryverseAnalytics({
  enabled,
  isOpen,
  episodeId,
  showId,
  apiClient,
  userId,
  onEpisodeStarted,
}) {
  const sessionIdRef = useRef(null);
  const flagsRef = useRef(createFlags());
  const trackingStoppedRef = useRef(false);
  const contentKeyRef = useRef(null);

  const resetTrackingState = useCallback(() => {
    sessionIdRef.current = null;
    flagsRef.current = createFlags();
    trackingStoppedRef.current = false;
  }, []);

  // Reset only when the episode/show changes (not on every open).
  useEffect(() => {
    if (!enabled || !episodeId || !showId) {
      resetTrackingState();
      contentKeyRef.current = null;
      return;
    }

    const key = `${episodeId}:${showId}`;
    if (contentKeyRef.current !== null && contentKeyRef.current !== key) {
      resetTrackingState();
    }
    contentKeyRef.current = key;
  }, [enabled, episodeId, showId, resetTrackingState]);

  // Reset when the player closes so the next open starts clean.
  useEffect(() => {
    if (!isOpen) {
      resetTrackingState();
    }
  }, [isOpen, resetTrackingState]);

  const beginSession = useCallback(() => {
    sessionIdRef.current = uuidv4();
    flagsRef.current = createFlags();
    trackingStoppedRef.current = false;
  }, []);

  const stopTracking = useCallback(() => {
    flagsRef.current.sentCompleted = true;
    trackingStoppedRef.current = true;
  }, []);

  const sendEvent = useCallback(
    async (eventType, watchSeconds, progressPercent) => {
      if (!enabled || !episodeId || !showId || !apiClient || !sessionIdRef.current) {
        return;
      }
      if (trackingStoppedRef.current) {
        return;
      }

      const flagKey = EVENT_TO_FLAG[eventType];
      if (flagKey && flagsRef.current[flagKey]) {
        return;
      }

      if (flagKey) {
        flagsRef.current[flagKey] = true;
      }
      if (eventType === 'episode_completed') {
        stopTracking();
      }

      const resolvedProgress =
        eventType === 'episode_completed' ? 100 : Math.min(100, Math.round(progressPercent * 100) / 100);

      const payload = {
        episode_id: episodeId,
        show_id: showId,
        event_type: eventType,
        watch_seconds: Math.round(watchSeconds * 100) / 100,
        progress_percent: resolvedProgress,
        session_id: sessionIdRef.current,
      };
      if (userId) {
        payload.user_id = userId;
      }

      console.log(`[Analytics] ${eventType}`, payload);

      try {
        await apiClient.sendStoryverseAnalyticsEvent(payload);
        if (eventType === 'episode_started' && onEpisodeStarted) {
          onEpisodeStarted(episodeId);
        }
      } catch (err) {
        if (flagKey) {
          flagsRef.current[flagKey] = false;
        }
        if (eventType === 'episode_completed') {
          trackingStoppedRef.current = false;
          flagsRef.current.sentCompleted = false;
        }
        console.error(`[Analytics] failed ${eventType}`, err);
      }
    },
    [enabled, episodeId, showId, apiClient, userId, onEpisodeStarted, stopTracking],
  );

  const onPlaybackStart = useCallback(
    (videoElement) => {
      if (!enabled || !videoElement) return;

      // Replay after a completed session, or first play — start a fresh session.
      if (trackingStoppedRef.current || !sessionIdRef.current) {
        beginSession();
      }

      if (flagsRef.current.sentStarted) {
        return;
      }

      const watchSeconds = videoElement.currentTime || 0;
      const duration = videoElement.duration || 0;
      const progress = duration > 0 ? (watchSeconds / duration) * 100 : 0;
      sendEvent('episode_started', watchSeconds, progress);
    },
    [enabled, beginSession, sendEvent],
  );

  const onTimeUpdate = useCallback(
    (videoElement) => {
      if (!enabled || !videoElement) return;
      if (trackingStoppedRef.current || flagsRef.current.sentCompleted) return;
      if (!sessionIdRef.current || !flagsRef.current.sentStarted) return;

      const duration = videoElement.duration;
      if (!duration || !Number.isFinite(duration)) return;

      const watchSeconds = videoElement.currentTime;
      const progressPercent = (watchSeconds / duration) * 100;

      if (progressPercent >= COMPLETION_THRESHOLD) {
        if (!flagsRef.current.sentCompleted) {
          const { watchSeconds, progressPercent: completedPercent } =
            getCompletionMetrics(videoElement);
          sendEvent('episode_completed', watchSeconds, completedPercent);
        }
        return;
      }

      if (progressPercent >= 25 && !flagsRef.current.sent25) {
        sendEvent('progress_25', watchSeconds, progressPercent);
      }
      if (progressPercent >= 50 && !flagsRef.current.sent50) {
        sendEvent('progress_50', watchSeconds, progressPercent);
      }
      if (progressPercent >= 75 && !flagsRef.current.sent75) {
        sendEvent('progress_75', watchSeconds, progressPercent);
      }
    },
    [enabled, sendEvent],
  );

  const onVideoEnded = useCallback(
    (videoElement) => {
      if (!enabled || !videoElement) return;
      if (trackingStoppedRef.current || flagsRef.current.sentCompleted) return;
      if (!sessionIdRef.current || !flagsRef.current.sentStarted) return;

      const { watchSeconds, progressPercent } = getCompletionMetrics(videoElement);
      sendEvent('episode_completed', watchSeconds, progressPercent);
    },
    [enabled, sendEvent],
  );

  return { onPlaybackStart, onTimeUpdate, onVideoEnded };
};
